import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  CreditCard,
  Building2,
  Link as LinkIcon,
  CheckCircle2,
  Zap,
  AlertCircle,
  QrCode,
  DollarSign,
  Send,
  FileCheck,
  Bell,
  Phone,
  ArrowRight
} from 'lucide-react';

export interface PaymentGatewayNodeData {
  paymentType?: 'bank_transfer' | 'payment_link' | 'both';
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  branchName?: string;
  paymentLinkUrl?: string;
  supportedCurrencies?: string;
  paymentInstructions?: string;
  autoSendOnOrder?: boolean;
  alertOwnerOnPaymentRequest?: boolean;
  forwardSlipToOwner?: boolean;
  ownerAlertPhone?: string;
  [key: string]: unknown;
}

export const PaymentGatewayNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as PaymentGatewayNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const persistNodeDataDirectly = (field: keyof PaymentGatewayNodeData, value: any, allUpdates?: Partial<PaymentGatewayNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'PAYMENT_GATEWAY',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[PaymentGatewayNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof PaymentGatewayNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const paymentType = nodeData.paymentType || 'both';
  const autoSendOnOrder = nodeData.autoSendOnOrder !== false;
  const alertOwnerOnPaymentRequest = nodeData.alertOwnerOnPaymentRequest !== false;
  const forwardSlipToOwner = nodeData.forwardSlipToOwner !== false;

  return (
    <div
      className={`group relative rounded-2xl border transition-all duration-300 backdrop-blur-xl min-w-[320px] max-w-[360px] bg-gradient-to-b from-[#081813]/98 via-[#05100D]/98 to-[#030806]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-emerald-400 ring-2 ring-emerald-400/50 shadow-[0_0_35px_rgba(16,185,129,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-emerald-500/40 hover:border-emerald-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(16,185,129,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('payment_gateway', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border-2 border-emerald-400 text-emerald-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.8)] hover:border-emerald-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view payment transactions"
          >
            <Zap size={13} className="text-emerald-300 animate-pulse" />
            <span>Manage Payments in Portal</span>
            <ArrowRight size={13} className="text-emerald-400" />
          </button>
        </div>
      )}
      {/* ── Connection Handles (4-Way Topology) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-payment"
        className="!w-4 !h-4 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 z-20 shadow-[0_0_12px_rgba(52,211,153,1)]"
        title="Input: Connect from Luna Core Hub"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-payment"
        className="!w-4 !h-4 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 z-20 shadow-[0_0_12px_rgba(52,211,153,1)]"
        title="Output: Forward to subsequent action"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!w-3.5 !h-3.5 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!w-3.5 !h-3.5 !bg-emerald-400 !border-2 !border-slate-900 transition-transform hover:!scale-125 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
      />

      {/* Header */}
      <div className="flex items-center justify-between p-3.5 border-b border-emerald-500/30 bg-gradient-to-r from-emerald-950/80 via-teal-950/60 to-slate-950/80 rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.3)]">
            <CreditCard className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="text-xs font-bold text-white tracking-wide uppercase">Payment Gateway</h4>
              {isWired && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <Zap className="w-2.5 h-2.5 mr-0.5 animate-pulse text-emerald-400" />
                  Live
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400">Bank Details & Payment Links</p>
          </div>
        </div>

        {/* Indicator */}
        <div className="flex items-center gap-1">
          {saveIndicator && (
            <span className="text-[9px] font-medium text-emerald-400 bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/30 animate-pulse">
              {saveIndicator}
            </span>
          )}
        </div>
      </div>

      {/* Live Badge Telemetry */}
      {typeof nodeData.dispatchedCount === 'number' && nodeData.dispatchedCount > 0 && (
        <div className="mx-3.5 mt-2 px-2 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/30 flex items-center justify-between text-[10px] text-emerald-300">
          <span className="flex items-center gap-1">
            <Send className="w-3 h-3 text-emerald-400" /> Dispatched to Callers:
          </span>
          <span className="font-bold text-white">{nodeData.dispatchedCount} times</span>
        </div>
      )}

      {/* Node Content */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-emerald-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard size={14} className="text-emerald-400" />
                <span className="font-bold text-white text-xs">{nodeData.bankName || 'Direct Payment Gateway'}</span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-200 border border-emerald-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-emerald-300 font-bold">
                💳 {nodeData.accountNumber ? `Acc: ${nodeData.accountNumber}` : 'Account Configured'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                {nodeData.paymentType === 'both' ? 'Bank & Link' : nodeData.paymentType === 'bank_transfer' ? 'Bank Transfer' : 'Online Link'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-3.5 space-y-3 text-xs">
        {/* Payment Method Selector */}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
            Payment Mode
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { id: 'both', label: 'Bank & Link', icon: Building2 },
              { id: 'bank_transfer', label: 'Bank Only', icon: Building2 },
              { id: 'payment_link', label: 'Link Only', icon: LinkIcon },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => handleChange('paymentType', mode.id)}
                className={`py-1.5 px-2 rounded-lg text-[10px] font-semibold border flex items-center justify-center gap-1 transition-all cursor-pointer ${
                  paymentType === mode.id
                    ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.3)]'
                    : 'bg-slate-800/60 border-slate-700/60 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <mode.icon className="w-3 h-3" />
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bank Transfer Details (if bank_transfer or both) */}
        {(paymentType === 'bank_transfer' || paymentType === 'both') && (
          <div className="space-y-2 p-2.5 rounded-xl bg-slate-950/50 border border-slate-800">
            <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
              <Building2 className="w-3 h-3" /> Bank Account Details
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9px] text-slate-400 block mb-0.5">Bank Name</label>
                <input
                  type="text"
                  value={nodeData.bankName || ''}
                  onChange={(e) => handleChange('bankName', e.target.value)}
                  placeholder="e.g. Commercial Bank"
                  className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="text-[9px] text-slate-400 block mb-0.5">Branch</label>
                <input
                  type="text"
                  value={nodeData.branchName || ''}
                  onChange={(e) => handleChange('branchName', e.target.value)}
                  placeholder="e.g. Colombo City"
                  className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[9px] text-slate-400 block mb-0.5">Account Name (Beneficiary)</label>
              <input
                type="text"
                value={nodeData.accountName || ''}
                onChange={(e) => handleChange('accountName', e.target.value)}
                placeholder="e.g. DTech Solutions (Pvt) Ltd"
                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div>
              <label className="text-[9px] text-slate-400 block mb-0.5">Account Number</label>
              <input
                type="text"
                value={nodeData.accountNumber || ''}
                onChange={(e) => handleChange('accountNumber', e.target.value)}
                placeholder="e.g. 8001234567"
                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
        )}

        {/* Online Payment Link (if payment_link or both) */}
        {(paymentType === 'payment_link' || paymentType === 'both') && (
          <div className="space-y-1.5 p-2.5 rounded-xl bg-slate-950/50 border border-slate-800">
            <div className="flex items-center gap-1 text-[10px] font-bold text-teal-400 uppercase tracking-wider">
              <LinkIcon className="w-3 h-3" /> Online Payment Link
            </div>
            <input
              type="url"
              value={nodeData.paymentLinkUrl || ''}
              onChange={(e) => handleChange('paymentLinkUrl', e.target.value)}
              placeholder="https://pay.kangavena.com/dtech or PayHere URL"
              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>
        )}

        {/* Payment Instructions / Slip Guidance */}
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
            Payment Notes & Slip Guidance
          </label>
          <textarea
            rows={2}
            value={nodeData.paymentInstructions || ''}
            onChange={(e) => handleChange('paymentInstructions', e.target.value)}
            placeholder="e.g. Please share the bank deposit slip or transaction receipt on WhatsApp once payment is made."
            className="w-full bg-slate-950/50 border border-slate-700/80 rounded-lg p-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none"
          />
        </div>

        {/* Business Owner WhatsApp Notifications & Slip Forwarding */}
        <div className="space-y-2 p-2.5 rounded-xl bg-slate-950/60 border border-emerald-500/20">
          <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
            <Bell className="w-3 h-3 text-emerald-400" /> Owner WhatsApp Notifications
          </div>

          {/* Toggle: Send captured payment details to owner */}
          <div className="flex items-center justify-between p-1.5 rounded-lg bg-slate-900/60 border border-slate-800">
            <div className="flex items-center gap-1.5 pr-2">
              <Send className="w-3 h-3 text-cyan-400 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-200 block font-medium">Send captured payment details to owner</span>
                <span className="text-[9px] text-slate-400 block leading-tight">Instant alert when customer requests bank/link</span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={alertOwnerOnPaymentRequest}
              onChange={(e) => handleChange('alertOwnerOnPaymentRequest', e.target.checked)}
              className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer rounded shrink-0"
            />
          </div>

          {/* Toggle: Send received payment slip to owner */}
          <div className="flex items-center justify-between p-1.5 rounded-lg bg-slate-900/60 border border-slate-800">
            <div className="flex items-center gap-1.5 pr-2">
              <FileCheck className="w-3 h-3 text-emerald-400 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-200 block font-medium">Send received payment slip to owner</span>
                <span className="text-[9px] text-slate-400 block leading-tight">Forwards deposit receipt image & details to owner</span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={forwardSlipToOwner}
              onChange={(e) => handleChange('forwardSlipToOwner', e.target.checked)}
              className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer rounded shrink-0"
            />
          </div>

          {/* Owner Alert WhatsApp Number */}
          {(alertOwnerOnPaymentRequest || forwardSlipToOwner) && (
            <div>
              <label className="text-[9px] text-slate-400 flex items-center gap-1 mb-1">
                <Phone className="w-2.5 h-2.5 text-emerald-400" />
                Owner WhatsApp Alert Phone
              </label>
              <input
                type="text"
                value={nodeData.ownerAlertPhone || ''}
                onChange={(e) => handleChange('ownerAlertPhone', e.target.value)}
                placeholder="e.g. +94767114888 (Defaults to Tenant Phone)"
                className="w-full bg-slate-900 border border-slate-700/80 rounded-lg px-2 py-1 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>
          )}
        </div>

        {/* Auto-Send on Voice Order Toggle */}
        <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/40 border border-slate-800">
          <div className="flex items-center gap-1.5">
            <FileCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[10px] text-slate-300">Auto-dispatch after Order</span>
          </div>
          <input
            type="checkbox"
            checked={autoSendOnOrder}
            onChange={(e) => handleChange('autoSendOnOrder', e.target.checked)}
            className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer rounded"
          />
        </div>
      </div>
      )}
    </div>
  );
});

PaymentGatewayNode.displayName = 'PaymentGatewayNode';
export default PaymentGatewayNode;
